import { useQuery } from "@tanstack/react-query";
import { useOutletContext, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { jobProgress, fmtDate } from "@/lib/constants";
import CreateJobDialog from "@/components/CreateJobDialog";
import RequestJobDialog from "@/components/RequestJobDialog";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";

export default function JobsPage() {
  const { user } = useOutletContext();
  const isManager = user.role === "owner" || user.role === "manager";
  const { data: jobs = [], isLoading } = useQuery({ queryKey: ["jobs"], queryFn: async () => (await api.get("/jobs")).data });

  const overall = (j) => {
    if (j.status === "requested") return "pending_review";
    if (j.stages.every((s) => s.status === "completed")) return "completed";
    if (j.stages.some((s) => s.status === "in_progress" || s.status === "completed")) return "in_progress";
    return "not_started";
  };

  return (
    <div className="space-y-6" data-testid="jobs-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow text-primary">Jobs</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Manufacturing Jobs</h1>
        </div>
        {isManager && <CreateJobDialog />}
        {user.role === "client" && <RequestJobDialog />}
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job ID</TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="hidden md:table-cell">Method Statement</TableHead>
                <TableHead className="hidden sm:table-cell">Captain</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>
              )}
              {!isLoading && jobs.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No jobs found.</TableCell></TableRow>
              )}
              {jobs.map((j) => {
                const prog = jobProgress(j);
                return (
                  <TableRow key={j.id} className="cursor-pointer" data-testid={`job-row-${j.id}`}>
                    <TableCell>
                      <Link to={`/jobs/${j.id}`} className="font-mono-tag text-primary hover:underline" data-testid={`job-link-${j.id}`}>
                        {j.job_number}
                      </Link>
                    </TableCell>
                    <TableCell className="font-medium">{j.client_name}</TableCell>
                    <TableCell className="hidden md:table-cell max-w-[260px] truncate text-muted-foreground">{j.ms_description}</TableCell>
                    <TableCell className="hidden sm:table-cell text-muted-foreground">{j.assigned_captain_name || "—"}</TableCell>
                    <TableCell className="text-sm">{j.current_stage}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-[90px]">
                        <Progress value={prog} className="h-1.5 w-16" />
                        <span className="text-xs font-mono-tag">{prog}%</span>
                      </div>
                    </TableCell>
                    <TableCell><StatusBadge status={overall(j)} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
